import { GridConfig } from '../types';

export const GRID_CONFIG: GridConfig = {
  majorGridSize: 100,
  minorGridSize: 20,
  majorGridColor: 0x636e72,
  minorGridColor: 0x95a5a6,
  majorGridAlpha: 0.3,
  minorGridAlpha: 0.1
};

/**
 * Rasterization resolution for viewport-scaled canvas labels. PIXI bakes each
 * Text into a bitmap once, so a value above 1 keeps labels crisp when zoomed in
 * (up to this multiple) at the cost of larger glyph textures.
 */
export const LABEL_RESOLUTION = 3;

/**
 * The KIWI Van Manufaktur brand palette (C.I. Guide 2023): gold and dark teal as
 * the primary colours, grey-green and red as the contrast colours. Reused for the
 * app chrome, canvas selection/highlights and the wire trunk.
 */
export const COLORS = {
  BACKGROUND: 0xf8f9fa,
  HEADER: '#002F38',          // dark teal (brand primary)
  PRIMARY: '#C8A45F',         // gold (brand primary)
  PRIMARY_HOVER: '#b8944f',
  SECONDARY: '#606A66',       // grey-green (brand contrast)
  SECONDARY_HOVER: '#525b57',
  DANGER: '#C94355',          // red (brand contrast)
  DANGER_HOVER: '#b23a4a',
  /** Primary action colour on light surfaces (readable with white text). */
  ACTION: '#002F38',
  ACTION_HOVER: '#0a4553',
  WIRE_BUNDLE: '#606A66'
} as const;

/** Brand palette as PIXI numeric colours, for canvas entities. */
export const BRAND = {
  GOLD: 0xc8a45f,
  TEAL: 0x002f38,
  GREY_GREEN: 0x606a66,
  RED: 0xc94355
} as const;

export const DIMENSIONS = {
  HEADER_HEIGHT: 60,
  NODE_RADIUS: 8,
  NODE_RADIUS_SELECTED: 12,
  WIRE_THICKNESS: 4
} as const;

export const CONNECTOR = {
  WIDTH: 200,
  HEIGHT: 150,
  CENTER_X: 100, // WIDTH / 2
  CENTER_Y: 75 // HEIGHT / 2
} as const;

/** Pin-gutter layout, shared by the connector rendering and wire anchoring. */
export const CONNECTOR_PINS = {
  BODY_Y: 40, // top of the first pin row, relative to the connector
  ROW_HEIGHT: 16,
  DOT_X: 12 // pin dot centre (gutter inset 8 + dot centre 4)
} as const;

/** Spacing of the wire fan-out and same-connector jumper bows. */
export const BUNDLE_GEOMETRY = {
  BREAKOUT_GAP: 36, // distance past the connector edge where wires merge into the trunk
  BOW_BULGE: 44 // how far a same-connector jumper arcs out past the left edge
} as const;

/** Defaults applied to newly created wires until edited in the wire table. */
export const WIRE_DEFAULTS = {
  COLOR: '#2d3436',
  GAUGE: 0.75, // mm²
  TYPE: 'FLY'
} as const;

export const VIEWPORT_CONSTRAINTS = {
  MIN_SCALE: 0.1,
  MAX_SCALE: 5.0,
  ZOOM_INTENSITY: 0.1,
  LERP_FACTOR: 0.15,
  /** Padding (screen px) left around the content when fitting it to the view. */
  FIT_PADDING: 80,
  /** Fit never zooms in past 100%, so a tiny graph is not blown up. */
  FIT_MAX_SCALE: 1.0
} as const;